import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// ── Single editable expense row ───────────────────────────────────────────────
const ExpenseRow = ({ expense, autoFocus, onUpdate, onDelete }) => {
  const [name,   setName]   = useState(expense.name);
  const [amount, setAmount] = useState(String(expense.amount));
  const nameRef = useRef(null);

  // Sync if parent refreshes data but don't clobber while user is typing
  useEffect(() => { setName(expense.name); },            [expense.name]);
  useEffect(() => { setAmount(String(expense.amount)); }, [expense.amount]);

  // Auto-focus and select all text in newly added rows
  useEffect(() => {
    if (autoFocus && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [autoFocus]);

  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed !== expense.name) onUpdate('name', trimmed);
  };

  const commitAmount = () => {
    const v = parseFloat(amount);
    if (!isNaN(v) && v >= 0 && v !== Number(expense.amount)) onUpdate('amount', v);
    else setAmount(String(expense.amount));
  };

  return (
    <div className="bizexp-row">
      <input
        ref={nameRef}
        className="bizexp-name-input"
        value={name}
        onChange={e => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={e => e.key === 'Enter' && e.target.blur()}
        placeholder="Expense name (e.g. Rent)"
      />
      <div className="bizexp-amount-wrapper">
        <span className="bizexp-dollar">$</span>
        <input
          className="bizexp-amount-input"
          type="number"
          min="0"
          step="0.01"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          onBlur={commitAmount}
          onKeyDown={e => e.key === 'Enter' && e.target.blur()}
        />
      </div>
      <button className="bizexp-delete-btn" onClick={onDelete} title="Remove">
        <i className="fas fa-trash-alt"></i>
      </button>
    </div>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────
const BusinessExpensesPanel = ({ onClose, onDataChange }) => {
  const now = new Date();
  const [year,        setYear]        = useState(now.getFullYear());
  const [month,       setMonth]       = useState(now.getMonth() + 1);
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [newId,       setNewId]       = useState(null); // ID of just-added expense

  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => { onDataChangeRef.current = onDataChange; });

  const fetchData = useCallback(async (y, m) => {
    setLoading(true);
    try {
      const { data: json } = await api.get(`/api/cogs/business-expenses?year=${y}&month=${m}`);
      setData(json);
      onDataChangeRef.current?.(json);
    } catch (e) {
      console.error('[BusinessExpenses] fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(year, month); }, [year, month, fetchData]);

  const addExpense = async () => {
    try {
      const { data: json } = await api.post('/api/cogs/business-expenses', { name: '', amount: 0 });
      if (json?.id) setNewId(json.id);
    } catch (e) {
      console.error('[BusinessExpenses] add error:', e);
    }
    fetchData(year, month);
  };

  const updateExpense = async (id, field, value) => {
    try {
      await api.put(`/api/cogs/business-expenses/${id}`, { [field]: value });
    } catch (e) {
      console.error('[BusinessExpenses] update error:', e);
    }
    fetchData(year, month);
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/api/cogs/business-expenses/${id}`);
    } catch (e) {
      console.error('[BusinessExpenses] delete error:', e);
    }
    fetchData(year, month);
  };

  const fmtUSD  = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtUnit = (v) => { const n = Number(v); return n === 0 ? '$0.00' : `$${parseFloat(n.toFixed(4))}`; };

  const expenses = data?.expenses ?? [];

  return (
    <div className="bizexp-overlay" onClick={e => { if (e.target.classList.contains('bizexp-overlay')) onClose(); }}>
      <div className="bizexp-panel">

        {/* Header */}
        <div className="bizexp-header">
          <span className="bizexp-title">
            <i className="fas fa-building"></i>
            Business Expenses
          </span>
          <button className="bizexp-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Period selector */}
        <div className="bizexp-period">
          <label className="bizexp-period-label">Period:</label>
          <select
            className="bizexp-period-select"
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <input
            className="bizexp-period-year"
            type="number"
            min="2020"
            max="2100"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          />
        </div>

        {/* Column headers */}
        <div className="bizexp-list-header">
          <span>Expense type</span>
          <span>Amount</span>
        </div>

        {/* Expense list — stays visible while reloading */}
        <div className="bizexp-list">
          {expenses.length === 0 && !loading && (
            <div className="bizexp-empty">No expenses yet. Click «Add expense» below.</div>
          )}
          {expenses.map(exp => (
            <ExpenseRow
              key={exp.id}
              expense={exp}
              autoFocus={exp.id === newId}
              onUpdate={(field, value) => updateExpense(exp.id, field, value)}
              onDelete={() => deleteExpense(exp.id)}
            />
          ))}
          {loading && (
            <div className="bizexp-refreshing">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
          )}
        </div>

        <button className="bizexp-add-btn" onClick={addExpense}>
          <i className="fas fa-plus"></i>
          Add expense
        </button>

        {/* Summary */}
        {data && (
          <div className="bizexp-summary">
            <div className="bizexp-summary-row">
              <span>Total expenses</span>
              <strong>{fmtUSD(data.totalExpenses)}</strong>
            </div>
            <div className="bizexp-summary-row">
              <span>Units produced ({MONTHS[month - 1]} {year})</span>
              <strong>{Number(data.totalQty).toLocaleString()}</strong>
            </div>
            <div className="bizexp-summary-row bizexp-summary-row--highlight">
              <span>Cost per unit</span>
              <strong>{fmtUnit(data.costPerUnit)}</strong>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BusinessExpensesPanel;
